import { faBoxes } from '@fortawesome/pro-regular-svg-icons'
import { ActionIcon } from '@mantine/core'
import {
  useEffect,
  useState,
  type ComponentProps,
  type ComponentType,
} from 'react'

import {
  type UNSAFE_any,
  type CoreProduct,
  type ParameterID,
} from '@flecto/types'

import { Badge, type BadgeProps } from '../Badge/Badge'
import { Icon } from '../Icon/Icon'
import { PopoverProductBundleItems } from '../PopoverProductBundleItems/PopoverProductBundleItems'
import { usePopoverTrackedState } from '../PopoverTracked/usePopoverTrackedState'
import classes from './styles.module.css'

export type BadgeProductBundlePopoverDefault = typeof PopoverProductBundleItems

export type BadgeProductBundlePopoverOverrideProps<
  C extends ComponentType<UNSAFE_any> = typeof PopoverProductBundleItems,
> = {
  popoverComponent?: C
  popoverProps?: ComponentProps<C>
}

export type BadgeProductBundleProps<
  C extends ComponentType<UNSAFE_any> = typeof PopoverProductBundleItems,
> = BadgeProps &
  BadgeProductBundlePopoverOverrideProps<C> & {
    product?: CoreProduct
    onItemClick?: (itemId: ParameterID) => void
  }

export function BadgeProductBundle<
  C extends ComponentType<UNSAFE_any> = BadgeProductBundlePopoverDefault,
>({
  product,
  size = 'sm',
  popoverComponent = PopoverProductBundleItems as C,
  popoverProps,
  onItemClick,
  ...props
}: BadgeProductBundleProps<C>) {
  const { popoverIdOpened, setPopoverIdOpened, isNonExclusive } =
    usePopoverTrackedState()

  const popoverId = `popover-badge-bundle-${product?.id}`
  const [localOpened, setLocalOpened] = useState(false)

  const opened = isNonExclusive()
    ? localOpened
    : localOpened && popoverIdOpened === popoverId

  useEffect(() => {
    if (
      !isNonExclusive() &&
      popoverIdOpened &&
      popoverIdOpened !== popoverId &&
      localOpened
    ) {
      setLocalOpened(false)
    }
  }, [popoverIdOpened, popoverId, localOpened])

  if (!product?.bundle) {
    return <></>
  }

  const PopoverComponent = popoverComponent
  const popProps = {
    product,
    onItemClick,
    opened,
    onChange: (newOpened: boolean) => {
      setLocalOpened(newOpened)
      if (!isNonExclusive()) {
        if (newOpened) {
          setPopoverIdOpened(popoverId)
        } else {
          setPopoverIdOpened(null)
        }
      }
    },
    ...(popoverProps || {}),
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    event.preventDefault()
    const newOpened = !opened
    setLocalOpened(newOpened)
    if (!isNonExclusive()) {
      if (newOpened) {
        setPopoverIdOpened(popoverId)
      } else {
        setPopoverIdOpened(null)
      }
    }
  }

  return (
    // @ts-expect-error PopoverProductBundleItems without types
    <PopoverComponent data-testid='popover' {...popProps}>
      <ActionIcon variant='transparent' onClick={handleClick} h='auto' w='auto'>
        <Badge
          data-testid='badge'
          size={size}
          color='gray'
          className={classes.badge}
          {...props}
          leftSection={<Icon icon={faBoxes} />}
        />
      </ActionIcon>
    </PopoverComponent>
  )
}
